import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FileUploadArea = ({ files, onFilesChange, errors }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);

  const acceptedTypes = {
    'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
    'video/*': ['.mp4', '.mov', '.avi', '.wmv'],
    '.dwg': [],
    '.step': [],
    '.iges': [],
    '.3dm': []
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFiles(e?.dataTransfer?.files);
    }
  };

  const handleFiles = (fileList) => {
    const newFiles = Array.from(fileList)?.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file?.name,
      size: file?.size,
      type: file?.type,
      progress: 0,
      status: 'uploading'
    }));

    // Simulate upload progress
    newFiles?.forEach(fileObj => {
      simulateUpload(fileObj?.id);
    });

    onFilesChange([...files, ...newFiles]);
  };

  const simulateUpload = (fileId) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 15;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));
        // Update file status to completed
        onFilesChange(prevFiles => 
          prevFiles?.map(f => 
            f?.id === fileId ? { ...f, status: 'completed', progress: 100 } : f
          )
        );
      } else {
        setUploadProgress(prev => ({ ...prev, [fileId]: progress }));
        onFilesChange(prevFiles => 
          prevFiles?.map(f => 
            f?.id === fileId ? { ...f, progress } : f
          )
        );
      }
    }, 200);
  };

  const removeFile = (fileId) => {
    onFilesChange(files?.filter(f => f?.id !== fileId));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress?.[fileId];
      return newProgress;
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return 'Image';
    if (type?.startsWith('video/')) return 'Video';
    if (type?.includes('dwg') || type?.includes('step') || type?.includes('iges')) return 'Box';
    return 'File';
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 elevation-1">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="Upload" size={20} color="var(--color-accent)" />
        </div>
        <div>
          <h3 className="text-lg font-heading text-foreground">Design Assets</h3>
          <p className="text-sm text-muted-foreground">Upload renderings, CAD files, and videos</p>
        </div>
      </div>
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-luxury ${
          dragActive 
            ? 'border-accent bg-accent/5' :'border-border hover:border-accent/50 hover:bg-accent/5'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*,.dwg,.step,.iges,.3dm"
          onChange={(e) => handleFiles(e?.target?.files)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        
        <div className="space-y-4">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto">
            <Icon name="CloudUpload" size={24} color="var(--color-muted-foreground)" />
          </div>
          
          <div>
            <p className="text-lg font-medium text-foreground mb-2">
              Drop files here or click to browse
            </p>
            <p className="text-sm text-muted-foreground">
              Supports images, videos, CAD files (DWG, STEP, IGES) up to 500MB each
            </p>
          </div>
          
          <Button
            variant="outline"
            onClick={() => fileInputRef?.current?.click()}
            iconName="Plus"
            iconPosition="left"
          >
            Select Files
          </Button>
        </div>
      </div>
      {errors?.files && (
        <p className="text-sm text-error mt-2">{errors?.files}</p>
      )}
      {/* File List */}
      {files?.length > 0 && (
        <div className="mt-6 space-y-3">
          <h4 className="text-sm font-medium text-foreground">Uploaded Files ({files?.length})</h4>
          
          <div className="space-y-2">
            {files?.map((fileObj) => (
              <div key={fileObj?.id} className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                <div className="w-8 h-8 bg-background rounded flex items-center justify-center flex-shrink-0">
                  <Icon name={getFileIcon(fileObj?.type)} size={16} color="var(--color-muted-foreground)" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{fileObj?.name}</p>
                  <p className="text-xs text-muted-foreground">{formatFileSize(fileObj?.size)}</p>
                  
                  {fileObj?.status === 'uploading' && (
                    <div className="mt-1">
                      <div className="w-full bg-border rounded-full h-1">
                        <div 
                          className="bg-accent h-1 rounded-full transition-all duration-300"
                          style={{ width: `${fileObj?.progress || 0}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  {fileObj?.status === 'completed' && (
                    <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                  )}
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFile(fileObj?.id)}
                    className="w-6 h-6"
                  >
                    <Icon name="X" size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadArea;